// Attendance Tracker — the coach sidebar's "Attendance" tab. Self-fetching
// (like SyllabusTracker/TaskTracker), scoped to one Balagruha at a time via
// its own dropdown.
//
// The date range picker drives which table column is shown:
//  - startDate === endDate (a single day, e.g. "today"): an "Attendance"
//    column with inline Present/Absent buttons for marking that day.
//  - a wider range: a "Classes Attended (out of total)" column, where
//    "total" is the number of days anyone in the Balagruha was marked in
//    that range (not every calendar day).
// Add/Edit/quick-mark all write to `endDate` — the popup has no date field
// of its own, so it always targets the last day of whatever range is
// currently selected (a single-day view's only day, in the common case).
//
// The Present/Absent stat cards: in single-day view they show that day's
// raw present/absent counts (a percentage over one day isn't meaningful),
// otherwise a student counts as present when their attendance over the
// range is at least PRESENT_THRESHOLD percent, and absent below it.
import React, { useEffect, useMemo, useState } from 'react';
import {
    CalendarCheck,
    Search,
    Plus,
    Pencil,
    Trash2,
    X,
    RotateCcw,
    Users,
} from 'lucide-react';
import './AttendanceTracker.css';
import {
    getAttendanceSummary,
    saveAttendanceEntry,
    deleteAttendanceEntry,
} from '../../api';
import showToast from '../../utils/toast';
import SearchableSelect from '../common/SearchableSelect';

const ITEMS_PER_PAGE = 8;
const PRESENT_THRESHOLD = 75;

const pad2 = (n) => String(n).padStart(2, '0');
const toISODateString = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const todayISODate = () => toISODateString(new Date());

const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: toISODateString(start), end: toISODateString(end) };
};

const formatDisplayDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Add/Edit popup — a right-side drawer with no date field of its own; it
// always records against `referenceDateLabel`'s day (the selected range's
// endDate), shown as a hint under the student picker instead.
function AttendanceEntryModal({
    student,
    students,
    referenceDateLabel,
    initialStatus,
    initialHomework,
    initialRemark,
    initialImprovement,
    onSave,
    onClose,
}) {
    const isLocked = !!student;
    const [studentId, setStudentId] = useState(student?.studentId || '');
    const [status, setStatus] = useState(initialStatus || 'present');
    const [homework, setHomework] = useState(initialHomework || '');
    const [remark, setRemark] = useState(initialRemark || '');
    const [improvementNeeded, setImprovementNeeded] = useState(initialImprovement || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!studentId) {
            showToast('Please select a student', 'error');
            return;
        }
        setSaving(true);
        try {
            await onSave({ studentId, status, homework, remark, improvementNeeded });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="attendance-modal-overlay" onClick={onClose}>
            <div className="attendance-modal" onClick={(e) => e.stopPropagation()}>
                <div className="attendance-modal-header">
                    <h3>{isLocked ? 'Edit Attendance' : 'Add Attendance'}</h3>
                    <button type="button" className="attendance-modal-close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                <div className="attendance-modal-body">
                    <div className="attendance-modal-field">
                        <label htmlFor="attendance-modal-student">Student Name{!isLocked && <> <span className="attendance-modal-required">*</span></>}</label>
                        {isLocked ? (
                            <div className="attendance-modal-readonly">{student.name}</div>
                        ) : (
                            <SearchableSelect
                                searchPlaceholder="Search student..."
                                id="attendance-modal-student"
                                value={studentId}
                                onChange={(e) => setStudentId(e.target.value)}
                            >
                                <option value="">Select student</option>
                                {(students || []).map((s) => (
                                    <option key={s.studentId} value={s.studentId}>{s.name}</option>
                                ))}
                            </SearchableSelect>
                        )}
                        <div className="attendance-modal-date-hint">Recording for {referenceDateLabel}</div>
                    </div>

                    <div className="attendance-modal-field">
                        <label>Attendance <span className="attendance-modal-required">*</span></label>
                        <div className="attendance-modal-status-toggle">
                            <button
                                type="button"
                                className={`attendance-status-btn present ${status === 'present' ? 'selected' : ''}`}
                                onClick={() => setStatus('present')}
                            >
                                Present
                            </button>
                            <button
                                type="button"
                                className={`attendance-status-btn absent ${status === 'absent' ? 'selected' : ''}`}
                                onClick={() => setStatus('absent')}
                            >
                                Absent
                            </button>
                        </div>
                    </div>

                    <div className="attendance-modal-field">
                        <label htmlFor="attendance-modal-homework">Homework</label>
                        <textarea
                            id="attendance-modal-homework"
                            rows={2}
                            placeholder="Enter homework details..."
                            value={homework}
                            onChange={(e) => setHomework(e.target.value)}
                        />
                    </div>

                    <div className="attendance-modal-field">
                        <label htmlFor="attendance-modal-remark">Remark</label>
                        <textarea
                            id="attendance-modal-remark"
                            rows={2}
                            placeholder="Enter remark..."
                            value={remark}
                            onChange={(e) => setRemark(e.target.value)}
                        />
                    </div>

                    <div className="attendance-modal-field">
                        <label htmlFor="attendance-modal-improvement">Improvement Needed</label>
                        <textarea
                            id="attendance-modal-improvement"
                            rows={2}
                            placeholder="Enter areas of improvement..."
                            value={improvementNeeded}
                            onChange={(e) => setImprovementNeeded(e.target.value)}
                        />
                    </div>
                </div>

                <div className="attendance-modal-footer">
                    <button type="button" className="attendance-modal-btn-cancel" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="button" className="attendance-modal-btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AttendanceTracker({ balagruhas, selectedBalagruhas, canEdit = false }) {
    const [balagruhaId, setBalagruhaId] = useState(selectedBalagruhas?.[0] || balagruhas?.[0]?._id || '');
    const defaultRange = useMemo(() => {
        const today = todayISODate();
        return { start: today, end: today };
    }, []);
    const [startDate, setStartDate] = useState(defaultRange.start);
    const [endDate, setEndDate] = useState(defaultRange.end);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalMode, setModalMode] = useState(null); // null | { mode: 'add' } | { mode: 'edit', student }
    const [markingIds, setMarkingIds] = useState([]); // students whose quick-mark save is in flight

    useEffect(() => {
        if (!balagruhaId && (selectedBalagruhas?.[0] || balagruhas?.[0]?._id)) {
            setBalagruhaId(selectedBalagruhas?.[0] || balagruhas?.[0]?._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBalagruhas, balagruhas]);

    const isSingleDayView = startDate === endDate;

    const fetchSummary = async () => {
        if (!balagruhaId || !startDate || !endDate) {
            setStudents([]);
            return;
        }
        setLoading(true);
        try {
            const response = await getAttendanceSummary({ balagruhaId, startDate, endDate });
            setStudents(response?.data?.students || []);
        } catch (error) {
            showToast('Failed to load attendance data', 'error');
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [balagruhaId, startDate, endDate]);

    useEffect(() => {
        setCurrentPage(1);
    }, [balagruhaId, startDate, endDate, searchText]);

    const presentCount = isSingleDayView
        ? students.filter((s) => s.referenceStatus === 'present').length
        : students.filter((s) => s.percentage >= PRESENT_THRESHOLD).length;
    const absentCount = isSingleDayView
        ? students.filter((s) => s.referenceStatus === 'absent').length
        : students.filter((s) => s.percentage < PRESENT_THRESHOLD).length;
    const totalStudents = students.length;

    const searchLower = searchText.trim().toLowerCase();
    const filteredStudents = students.filter((s) => (s.name || '').toLowerCase().includes(searchLower));

    const totalPages = Math.max(1, Math.ceil(filteredStudents.length / ITEMS_PER_PAGE));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginatedStudents = filteredStudents.slice(
        (pageSafe - 1) * ITEMS_PER_PAGE,
        pageSafe * ITEMS_PER_PAGE
    );

    const handleClearFilters = () => {
        const range = getCurrentMonthRange();
        setBalagruhaId(selectedBalagruhas?.[0] || balagruhas?.[0]?._id || '');
        setStartDate(range.start);
        setEndDate(range.end);
        setSearchText('');
    };

    const handleSetToday = () => {
        const today = todayISODate();
        setStartDate(today);
        setEndDate(today);
    };

    const handleSaveEntry = async ({ studentId, status, homework, remark, improvementNeeded }) => {
        try {
            await saveAttendanceEntry({
                balagruhaId,
                studentId,
                date: endDate,
                status,
                homework,
                remark,
                improvementNeeded,
            });
            showToast('Attendance saved', 'success');
            setModalMode(null);
            fetchSummary();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to save attendance', 'error');
        }
    };

    // Single-day Present/Absent buttons. Only `status` is sent, so the
    // backend keeps any homework/remark/improvement notes already saved for
    // the day. The row is patched in place rather than refetched so the
    // table doesn't flash "Loading…" on every click; single-day view only
    // reads referenceStatus, and changing the range refetches anyway.
    const handleQuickMark = async (student, status) => {
        const { studentId } = student;
        if (!canEdit || student.referenceStatus === status || markingIds.includes(studentId)) return;
        setMarkingIds((ids) => [...ids, studentId]);
        try {
            await saveAttendanceEntry({ balagruhaId, studentId, date: endDate, status });
            setStudents((list) => list.map((s) => (s.studentId === studentId ? { ...s, referenceStatus: status } : s)));
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to mark attendance', 'error');
        } finally {
            setMarkingIds((ids) => ids.filter((id) => id !== studentId));
        }
    };

    const handleDelete = async (student) => {
        if (!window.confirm(`Remove ${formatDisplayDate(endDate)}'s attendance record for ${student.name}?`)) return;
        try {
            await deleteAttendanceEntry({ studentId: student.studentId, date: endDate });
            showToast('Attendance record removed', 'success');
            fetchSummary();
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to remove attendance record', 'error');
        }
    };

    return (
        <div className="attendance-tracker">
            <div className="attendance-tracker-header">
                <div className="attendance-tracker-title">
                    <span className="attendance-tracker-title-icon" aria-hidden="true">
                        <CalendarCheck size={24} color="#0f9d8c" strokeWidth={2.25} />
                    </span>
                    <div className="attendance-tracker-title-text">
                        <h3>Attendance</h3>
                        <div className="attendance-tracker-subtitle">Track student attendance, homework and progress notes.</div>
                    </div>
                </div>

                {canEdit && (
                    <button
                        type="button"
                        className="attendance-tracker-add-btn"
                        onClick={() => setModalMode({ mode: 'add' })}
                        disabled={!balagruhaId}
                    >
                        <Plus size={16} strokeWidth={2.5} /> Add Attendance
                    </button>
                )}
            </div>

            {/* Filters get their own row so the Balgruha name and both dates
                are always shown in full instead of being squeezed beside the title. */}
            <div className="attendance-tracker-toolbar">
                <label className="attendance-tracker-context-pill">
                    <span className="attendance-tracker-context-label">Balgruha</span>
                    <SearchableSelect
                        searchPlaceholder="Search balagruha..."
                        className="attendance-tracker-context-select"
                        value={balagruhaId}
                        onChange={(e) => setBalagruhaId(e.target.value)}
                    >
                        {(balagruhas || []).map((b) => (
                            <option key={b._id} value={b._id}>{b.name}</option>
                        ))}
                    </SearchableSelect>
                </label>

                <div className="attendance-tracker-context-pill attendance-tracker-date-pill">
                    <span className="attendance-tracker-context-label">Date Range</span>
                    <input
                        type="date"
                        aria-label="Start date"
                        value={startDate}
                        max={endDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                    <span className="attendance-tracker-date-sep">to</span>
                    <input
                        type="date"
                        aria-label="End date"
                        value={endDate}
                        min={startDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                    <button type="button" className="attendance-tracker-today-link" onClick={handleSetToday}>
                        Today
                    </button>
                </div>

                <button type="button" className="attendance-tracker-clear-btn" onClick={handleClearFilters}>
                    <RotateCcw size={14} /> Clear Filters
                </button>
            </div>

            <div className="attendance-tracker-stats-row">
                <div className="attendance-stat-card present">
                    <span className="attendance-stat-icon" aria-hidden="true"><Users size={20} /></span>
                    <div className="attendance-stat-text">
                        <div className="attendance-stat-label">Present (&ge;{PRESENT_THRESHOLD}%)</div>
                        <div className="attendance-stat-value">{presentCount}</div>
                    </div>
                </div>
                <div className="attendance-stat-card absent">
                    <span className="attendance-stat-icon" aria-hidden="true"><X size={20} strokeWidth={3} /></span>
                    <div className="attendance-stat-text">
                        <div className="attendance-stat-label">Absent (&lt;{PRESENT_THRESHOLD}%)</div>
                        <div className="attendance-stat-value">{absentCount}</div>
                    </div>
                </div>
                <div className="attendance-stat-card total">
                    <span className="attendance-stat-icon" aria-hidden="true"><Users size={20} /></span>
                    <div className="attendance-stat-text">
                        <div className="attendance-stat-label">Total Students</div>
                        <div className="attendance-stat-value">{totalStudents}</div>
                    </div>
                </div>
                <div className="attendance-tracker-search">
                    <Search size={16} className="attendance-tracker-search-icon" aria-hidden="true" />
                    <input
                        type="text"
                        placeholder="Search by student name..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            <div className="attendance-tracker-card">
                <div className="attendance-tracker-table-wrapper">
                    <table className="attendance-tracker-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th className="attendance-tracker-center-cell">
                                    {isSingleDayView ? 'Attendance' : 'Classes Attended (out of total)'}
                                </th>
                                <th>Homework</th>
                                <th>Remark</th>
                                <th>Improvement Needed</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="attendance-tracker-empty">Loading…</td></tr>
                            )}
                            {!loading && paginatedStudents.length === 0 && (
                                <tr><td colSpan={7} className="attendance-tracker-empty">No students found</td></tr>
                            )}
                            {!loading && paginatedStudents.map((student, index) => (
                                <tr key={student.studentId} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td>{(pageSafe - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                    <td>{student.name}</td>
                                    {isSingleDayView ? (
                                        <td className="attendance-tracker-center-cell">
                                            <div className="attendance-tracker-mark-buttons">
                                                {['present', 'absent'].map((status) => (
                                                    <button
                                                        key={status}
                                                        type="button"
                                                        className={`attendance-mark-btn ${status} ${student.referenceStatus === status ? 'selected' : ''}`}
                                                        onClick={() => handleQuickMark(student, status)}
                                                        disabled={!canEdit || markingIds.includes(student.studentId)}
                                                        aria-pressed={student.referenceStatus === status}
                                                        aria-label={`Mark ${student.name} ${status}`}
                                                    >
                                                        {status === 'present' ? 'Present' : 'Absent'}
                                                    </button>
                                                ))}
                                            </div>
                                        </td>
                                    ) : (
                                        <td
                                            className={`attendance-tracker-center-cell attendance-tracker-rate ${
                                                student.percentage >= PRESENT_THRESHOLD ? 'good' : 'low'
                                            }`}
                                        >
                                            {`${student.classesAttended} / ${student.totalClasses} (${student.percentage}%)`}
                                        </td>
                                    )}
                                    <td className="attendance-tracker-note-cell">{student.homework || '-'}</td>
                                    <td className="attendance-tracker-note-cell">{student.remark || '-'}</td>
                                    <td className="attendance-tracker-note-cell">{student.improvementNeeded || '-'}</td>
                                    <td className="attendance-tracker-actions-cell">
                                        {canEdit && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="attendance-tracker-edit-btn"
                                                    onClick={() => setModalMode({ mode: 'edit', student })}
                                                    aria-label={`Edit ${student.name}`}
                                                    title="Edit"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="attendance-tracker-delete-btn"
                                                    onClick={() => handleDelete(student)}
                                                    aria-label={`Delete ${student.name}'s attendance record`}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="attendance-tracker-footer">
                    <div className="attendance-tracker-footer-info">
                        {filteredStudents.length === 0
                            ? 'Showing 0 students'
                            : `Showing ${(pageSafe - 1) * ITEMS_PER_PAGE + 1} to ${Math.min(pageSafe * ITEMS_PER_PAGE, filteredStudents.length)} of ${filteredStudents.length} students`}
                    </div>
                    {totalPages > 1 && (
                        <div className="attendance-tracker-pagination">
                            <button type="button" disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>‹</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={pageSafe === i + 1 ? 'active' : ''}
                                    onClick={() => setCurrentPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button type="button" disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>›</button>
                        </div>
                    )}
                </div>
            </div>

            {modalMode?.mode === 'add' && (
                <AttendanceEntryModal
                    students={students}
                    referenceDateLabel={formatDisplayDate(endDate)}
                    onSave={handleSaveEntry}
                    onClose={() => setModalMode(null)}
                />
            )}

            {modalMode?.mode === 'edit' && (
                <AttendanceEntryModal
                    student={modalMode.student}
                    referenceDateLabel={formatDisplayDate(endDate)}
                    initialStatus={modalMode.student.referenceStatus || 'present'}
                    initialHomework={modalMode.student.homework}
                    initialRemark={modalMode.student.remark}
                    initialImprovement={modalMode.student.improvementNeeded}
                    onSave={handleSaveEntry}
                    onClose={() => setModalMode(null)}
                />
            )}
        </div>
    );
}

export default AttendanceTracker;
